<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <div
    class="kpi"
    :class="classes">
    <div
      class="icon"
      :style="iconStyle">
      <slot name="icon" />
      <component :is="icon" />
    </div>
    <div
      v-tooltip="tooltip"
      class="right">
      <div class="value-container">
        <loader-small v-if="isLoading" />
        <icon-filter
          v-show="isLoading === false && isFiltered === true"
          class="icon-filter" />
        <div
          class="value-and-trend"
          :style="valueAndTrendStyle">
          <div
            class="value"
            :class="{ money: isMoney }"
            :style="valueStyle">
            <slot>
              {{ value }}
            </slot>
          </div>
          <div
            v-if="trend !== null && trend != 0 && !Number.isNaN(trend)"
            class="trend badge"
            :class="trendClasses"
            :style="trendStyle">
            {{ trendFormatted }}
          </div>
        </div>
      </div>
      <div
        :class="labelClasses"
        :style="labelStyle">
        <span>{{ label }}</span>
        <span style="text-transform: initial">
          <slot name="label-badge" />
        </span>
      </div>
    </div>
  </div>
</template>

<script type="text/babel">
  import { LoaderSmall } from '@/common/components/controls';
  import {
    IconColoredAdSpend,
    IconColoredAdvertiser,
    IconColoredAirplane,
    IconColoredBudget,
    IconColoredBuyer,
    IconColoredCalendar,
    IconColoredCPM,
    IconColoredCreative,
    IconColoredDeal,
    IconColoredDemographic,
    IconColoredEstimatedCPM,
    IconColoredFilter,
    IconColoredImpression,
    IconColoredImpressionsDelivered,
    IconColoredLineItem,
    IconColoredNetwork,
    IconColoredPacing,
    IconColoredPacingStrategyEven,
    IconColoredPendingApproval,
    IconColoredPlayHexagon,
    IconColoredProgress,
    IconColoredScreen,
    IconColoredSite,
    IconColoredUnit,
  } from '@/common/icons/colored';
  import {
    IconAdSpend,
    IconBudget,
    IconCPM,
    IconCreative,
    IconDeal,
    IconFilter,
    IconImpressionsDelivered,
    IconLineItem,
    IconPacing,
    IconPendingApproval,
    IconProgress,
  } from '@/common/icons/monochrome';
  import formatNumber from '@/utils/NumberFormatter';

  export default {
    components: {
      IconAdSpend,
      IconBudget,
      'icon-cpm': IconCPM,
      IconCreative,
      IconDeal,
      IconFilter,
      IconImpressionsDelivered,
      IconLineItem,
      IconPacing,
      IconColoredCalendar,
      IconPendingApproval,
      IconProgress,
      IconColoredAirplane,
      IconColoredAdSpend,
      IconColoredAdvertiser,
      IconColoredBudget,
      IconColoredBuyer,
      'icon-colored-cpm': IconColoredCPM,
      IconColoredCreative,
      IconColoredDeal,
      IconColoredDemographic,
      'icon-colored-estimated-cpm': IconColoredEstimatedCPM,
      IconColoredFilter,
      IconColoredImpression,
      IconColoredImpressionsDelivered,
      IconColoredLineItem,
      IconColoredNetwork,
      IconColoredPacing,
      IconColoredPacingStrategyEven,
      IconColoredPendingApproval,
      IconColoredPlayHexagon,
      IconColoredProgress,
      IconColoredScreen,
      IconColoredSite,
      IconColoredUnit,
      LoaderSmall,
    },
    props: {
      value: {
        type: String,
      },
      trend: {
        type: Number,
        default: null,
      },
      label: {
        type: String,
      },
      labelStyle: {
        type: String,
        default: null,
      },
      icon: {
        default: null,
      },
      isFiltered: {
        type: Boolean,
        default: false,
      },
      isLoading: {
        type: Boolean,
        default: false,
      },
      bigIcon: {
        type: Boolean,
        default: false,
      },
      isMoney: {
        type: Boolean,
        default: false,
      },
      valueStyle: {
        type: String,
        default: null,
      },
      trendFormat: {
        type: String,
        default: null,
      },
      trendStyle: {
        type: String,
        default: null,
      },
      trendFadeDelay: {
        type: Number,
        default: null,
      },
      iconStyle: {
        type: String,
        default: null,
      },
      noUppercase: {
        type: Boolean,
        default: false,
      },
      tooltip: {
        type: String,
        default: null,
      },
      useFlexBasis: {
        type: Boolean,
        default: true,
      },
    },
    computed: {
      classes() {
        let classNames = this.isFiltered === true ? 'filtered' : '';
        if (this.bigIcon) {
          classNames += ' big-icon';
        } else {
          classNames += ' small';
        }
        if (!this.useFlexBasis) {
          classNames += ' no-flex-basis';
        }
        return classNames;
      },
      labelClasses() {
        let classes = 'label';
        if (this.noUppercase) {
          classes = `${classes} no-uppercase`;
        }
        return classes;
      },
      trendFormatted() {
        if (this.trend === null) {
          return null;
        }
        let format = this.trendFormat;
        if (format == null) {
          format = this.trend > 1 ? '+0,0a%' : '+0.0a%';
        }
        return formatNumber(this.trend, format);
      },
      trendClasses() {
        let classes = '';
        if (this.trendFadeDelay != null) {
          classes = `${classes} fade-out-trend`;
        }
        if (this.trend > 0) {
          classes = `${classes} badge-success`;
        } else if (this.trend < 0) {
          classes = `${classes} badge-danger`;
        } else {
          classes = `${classes} badge-default`;
        }
        return classes;
      },
      valueAndTrendStyle() {
        return this.isLoading ? 'opacity: 0; transform: scaleX(0)' : '';
      },
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  .kpi {
    display: flex;
    flex-direction: column;
    flex: 1 0 10%;
    margin: 1.6rem 2rem;
    align-items: center;
    align-content: center;

    &:hover {
      .fade-out-trend {
        opacity: 1;
        animation: none;
      }
    }

    &.filtered {
      color: var(--error);

      .value-container,
      .icon {
      }
    }

    .value-container {
      font-size: 2.8rem;
      text-align: center;
      font-weight: 600;
      display: flex;
      align-items: center;

      svg {
        height: 1.4rem;
        position: relative;
        top: -0.8rem;
      }

      .icon-filter {
        top: 0;

        fill: var(--error);
        stroke: var(--error);
      }

      .value-and-trend {
        display: flex;
        align-items: center;

        .trend {
          font-size: 0.6em;
          margin-left: 0.6rem;
        }
      }
    }

    .label {
      color: var(--textMutedColor);

      text-transform: uppercase;
      font-weight: 600;
      font-size: 1.3rem;

      &.no-uppercase {
        text-transform: none !important;
      }

      svg {
        stroke: var(--textColorDark);

        width: 1.6rem;
        height: 1.6rem;
        margin-right: 0.3rem;
        position: relative;
        top: 0.2rem;
      }
    }

    &.big-icon {
      flex-direction: row;
      align-items: center;
      align-content: center;

      .icon {
        margin-right: 2rem;

        svg {
          width: 4.8rem;
          height: 4.8rem;
          margin-right: 0.3rem;
          position: relative;
          top: 0.2rem;
        }
      }

      .label {
        text-align: left;
      }

      .value-container {
        text-align: left;
      }
    }
  }

  @media (max-width: 1400px) {
    .kpi {
      .value-container {
        font-size: 2.3rem;
      }

      &.big-icon {
        .icon {
          margin-right: 1rem;

          svg {
            width: 4rem;
            height: 4rem;
            margin-right: 0.3rem;
            position: relative;
            top: 0.2rem;
          }
        }
      }
    }
  }

  @media (max-width: 1023px) {
    .kpi {
      flex-basis: 33%;

      &.no-flex-basis {
        flex-basis: 0;
      }

      .value-container {
        font-size: 1.7rem;
      }

      &.big-icon {
        .icon {
          margin-right: 1rem;

          svg {
            width: 3.2rem;
            height: 3.2rem;
            margin-right: 0.3rem;
            position: relative;
            top: 0.2rem;
          }
        }
      }
    }
  }

  .kpi.small {
    flex-basis: 33%;
    flex-direction: row;
    margin: 0.6rem 1.2rem;

    &.no-flex-basis {
      flex-basis: 0;
    }

    .value-container {
      font-size: 1.4rem;
      text-align: left;

      .loader {
        height: 1.6rem;
        width: 1.6rem;
      }
    }

    .icon {
      margin-right: 1rem;

      svg {
        width: 2.4rem;
        height: 2.4rem;
        margin-right: 0.3rem;
      }
    }

    .label {
      font-size: 0.9em;
    }
  }

  .fade-out-trend {
    animation: fade-out-trend 1s 1;
    -webkit-animation: fade-out-trend 1s 1;
    animation-fill-mode: forwards;
    animation-timing-function: cubic-bezier(0.2, 1, 0.2, 1);
    animation-delay: 2s;
    -webkit-animation-delay: 2s; /* Safari and Chrome */
    -webkit-animation-fill-mode: forwards;
  }

  @keyframes fade-out-trend {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }

  @-webkit-keyframes fade-out-trend {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }
</style>
